import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber, Tag, Card, Row, Col, Statistic, Space, Popconfirm, message, Tabs } from 'antd';
import { PlusOutlined, ToolOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = '/api/equipment';
const { Option } = Select;
const { TextArea } = Input;

const EquipmentManagement = () => {
  const [tab, setTab] = useState('equipment');
  const [stats, setStats] = useState<any>({});
  const [equipList, setEquipList] = useState([]);
  const [maintList, setMaintList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [equipModal, setEquipModal] = useState(false);
  const [maintModal, setMaintModal] = useState(false);
  const [editEquip, setEditEquip] = useState(null);
  const [equipForm] = Form.useForm();
  const [maintForm] = Form.useForm();
  const [keyword, setKeyword] = useState('');

  useEffect(() => { loadStats(); loadEquip(); }, []);

  const loadStats = async () => {
    try { const r = await axios.get(`${API}/stats`); if (r.data.code === 0) setStats(r.data.data); } catch (e) {}
  };

  const loadEquip = async (kw = keyword) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/equipments`, { params: { pageSize: 100, keyword: kw } });
      if (r.data.code === 0) setEquipList(r.data.data.list);
    } catch (e) {}
    setLoading(false);
  };

  const loadMaint = async (status = '') => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/maintenance`, { params: { pageSize: 100, status } });
      if (r.data.code === 0) setMaintList(r.data.data.list);
    } catch (e) {}
    setLoading(false);
  };

  const handleTabChange = (key) => {
    setTab(key);
    if (key === 'maintenance' && maintList.length === 0) loadMaint();
  };

  const openEquipModal = (record = null) => {
    setEditEquip(record);
    if (record) {
      equipForm.setFieldsValue({ ...record, purchase_date: record.purchase_date || undefined, warranty_end: record.warranty_end || undefined });
    } else equipForm.resetFields();
    setEquipModal(true);
  };

  const saveEquip = async () => {
    const values = await equipForm.validateFields();
    try {
      if (editEquip) {
        await axios.put(`${API}/equipments/${editEquip.id}`, values);
        message.success('更新成功');
      } else {
        await axios.post(`${API}/equipments`, values);
        message.success('添加成功');
      }
      setEquipModal(false);
      loadEquip(); loadStats();
    } catch (e) { message.error('保存失败'); }
  };

  const deleteEquip = async (id) => {
    try {
      const r = await axios.delete(`${API}/equipments/${id}`);
      if (r.data.code === 0) { message.success('删除成功'); loadEquip(); loadStats(); }
      else message.error(r.data.message);
    } catch (e) { message.error('删除失败'); }
  };

  const openMaintModal = () => { maintForm.resetFields(); setMaintModal(true); };

  const saveMaint = async () => {
    const values = await maintForm.validateFields();
    try {
      await axios.post(`${API}/maintenance`, values);
      message.success('提交成功');
      setMaintModal(false);
      loadMaint(); loadStats();
    } catch (e) { message.error('提交失败'); }
  };

  const completeMaint = async (id) => {
    try {
      await axios.post(`${API}/maintenance/${id}/complete`, {});
      message.success('已完成');
      loadMaint(); loadStats();
    } catch (e) { message.error('操作失败'); }
  };

  const statusMap = { 0: { text: '正常', color: 'green' }, 1: { text: '维修中', color: 'orange' }, 2: { text: '停用', color: 'red' } };
  const maintStatusMap = { 0: { text: '待处理', color: 'red' }, 1: { text: '处理中', color: 'orange' }, 2: { text: '已完成', color: 'green' }, 3: { text: '已关闭', color: 'default' } };
  const typeMap = { 0: '日常保养', 1: '故障维修', 2: '大修' };
  const priorityMap = { 1: { text: '低', color: 'default' }, 2: { text: '中', color: 'blue' }, 3: { text: '高', color: 'orange' }, 4: { text: '紧急', color: 'red' } };

  const equipColumns = [
    { title: '设备名称', dataIndex: 'name', key: 'name' },
    { title: '编号', dataIndex: 'code', key: 'code' },
    { title: '分类', dataIndex: 'category', key: 'category' },
    { title: '位置', dataIndex: 'location', key: 'location' },
    { title: '品牌/型号', key: 'brand', render: (_, r) => `${r.brand || ''} ${r.model || ''}`.trim() || '-' },
    { title: '状态', dataIndex: 'status', key: 'status', render: v => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag> },
    { title: '负责人', dataIndex: 'responsible_person', key: 'responsible_person' },
    { title: '操作', key: 'action', render: (_, r) => (
      <Space>
        <Button size="small" type="link" onClick={() => openEquipModal(r)}>编辑</Button>
        <Button size="small" type="link" onClick={() => { maintForm.resetFields(); maintForm.setFieldsValue({ equipment_id: r.id }); setMaintModal(true); }}>报修</Button>
        <Popconfirm title="确定删除？" onConfirm={() => deleteEquip(r.id)}><Button size="small" type="link" danger>删除</Button></Popconfirm>
      </Space>
    )}
  ];

  const maintColumns = [
    { title: '设备', key: 'equipment', render: (_, r) => r.Equipment?.name || r.equipment?.name || '-' },
    { title: '类型', dataIndex: 'type', key: 'type', render: v => typeMap[v] || '-' },
    { title: '优先级', dataIndex: 'priority', key: 'priority', render: v => <Tag color={priorityMap[v]?.color}>{priorityMap[v]?.text}</Tag> },
    { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', render: v => <Tag color={maintStatusMap[v]?.color}>{maintStatusMap[v]?.text}</Tag> },
    { title: '费用', dataIndex: 'cost', key: 'cost', render: v => v > 0 ? `¥${v}` : '-' },
    { title: '提交时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '操作', key: 'action', render: (_, r) => (
      <Space>
        {r.status === 0 && <Button size="small" type="link" onClick={async () => { await axios.put(`${API}/maintenance/${r.id}`, { status: 1 }); loadMaint(); }}>开始处理</Button>}
        {r.status === 1 && <Popconfirm title="确认完成？" onConfirm={() => completeMaint(r.id)}><Button size="small" type="link">完成</Button></Popconfirm>}
      </Space>
    )}
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}><Card><Statistic title="设备总数" value={stats.totalEquipment || 0} /></Card></Col>
        <Col span={4}><Card><Statistic title="正常" value={stats.normalEquipment || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="维修中" value={stats.repairingEquipment || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="待维护" value={stats.pendingMaintenance || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
      </Row>

      <Tabs activeKey={tab} onChange={handleTabChange} items={[
        { key: 'equipment', label: '设备列表', children: (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <Input.Search placeholder="搜索设备" onSearch={v => { setKeyword(v); loadEquip(v); }} style={{ width: 300 }} />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openEquipModal()}>添加设备</Button>
            </div>
            <Table columns={equipColumns} dataSource={equipList} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} />
          </div>
        )},
        { key: 'maintenance', label: '维护工单', children: (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <Select placeholder="状态筛选" allowClear style={{ width: 120 }} onChange={v => loadMaint(v ?? '')}>
                <Option value="">全部</Option><Option value="0">待处理</Option><Option value="1">处理中</Option><Option value="2">已完成</Option>
              </Select>
              <Button type="primary" icon={<ToolOutlined />} onClick={openMaintModal}>报修/保养</Button>
            </div>
            <Table columns={maintColumns} dataSource={maintList} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} />
          </div>
        )}
      ]} />

      <Modal title={editEquip ? '编辑设备' : '添加设备'} open={equipModal} onOk={saveEquip} onCancel={() => setEquipModal(false)} width={600}>
        <Form form={equipForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="name" label="设备名称" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="code" label="设备编号"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="category" label="分类"><Input placeholder="如：加工设备" /></Form.Item></Col>
            <Col span={12}><Form.Item name="location" label="位置"><Input placeholder="如：1号车间" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="brand" label="品牌"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="model" label="型号"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="purchase_date" label="购入日期"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="warranty_end" label="保修截止"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="responsible_person" label="负责人"><Input /></Form.Item>
          <Form.Item name="status" label="状态"><Select><Option value={0}>正常</Option><Option value={1}>维修中</Option><Option value={2}>停用</Option></Select></Form.Item>
          <Form.Item name="remark" label="备注"><TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="报修/维护申请" open={maintModal} onOk={saveMaint} onCancel={() => setMaintModal(false)} width={500}>
        <Form form={maintForm} layout="vertical">
          <Form.Item name="equipment_id" label="设备" rules={[{ required: true }]}><Select placeholder="选择设备" showSearch optionFilterProp="children">
            {equipList.map(e => <Option key={e.id} value={e.id}>{e.name} {e.code ? `(${e.code})` : ''}</Option>)}
          </Select></Form.Item>
          <Form.Item name="type" label="类型" rules={[{ required: true }]}><Select><Option value={0}>日常保养</Option><Option value={1}>故障维修</Option><Option value={2}>大修</Option></Select></Form.Item>
          <Form.Item name="priority" label="优先级"><Select><Option value={1}>低</Option><Option value={2}>中</Option><Option value={3}>高</Option><Option value={4}>紧急</Option></Select></Form.Item>
          <Form.Item name="description" label="问题描述" rules={[{ required: true }]}><TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EquipmentManagement;
